import type { UserRolePermissions } from '@/services/userRoleService';

export type AccessLevel = string;
export type GeneralSettingsAccess = 'view' | 'configure';

export interface PermissionState {
  // Permission codes (e.g. 'dashboard.view_own')
  enabledPermissions: Set<string>;
  // Timesheet Access
  viewTimesheetsAccess: AccessLevel;
  editTimesheetsAccess: AccessLevel;
  allowDeleteTimesheets: boolean;
  allowDuplicateTimesheets: boolean;
  allowRecallTimesheet: boolean;
  // Smart Features
  enableCopyFromLastWeek: boolean;
  enableCopyDescription: boolean;
  enableCopyOnlyBillable: boolean;
  enableAutoSmartFill: boolean;
  enableAiInsights: boolean;
  // Approval
  viewApprovalDashboard: boolean;
  viewApprovals: boolean;
  editTimesheetsApproval: boolean;
  rejectTimesheets: boolean;
  compulsoryRejectionReason: boolean;
  bulkApprove: boolean;
  delegateApproval: boolean;
  viewPolicyViolations: boolean;
  viewDetectedIssues: boolean;
  // Reports
  enableTimesheetReport: boolean;
  viewTimesheetReportAccess: AccessLevel;
  allowTimesheetReportExport: boolean;
  enableMissingTimesheetReport: boolean;
  viewMissingTimesheetReportAccess: AccessLevel;
  allowMissingTimesheetReportExport: boolean;
  enableUtilisationReport: boolean;
  viewUtilisationReportAccess: AccessLevel;
  allowUtilisationReportExport: boolean;
  enableAuditLog: boolean;
  viewAuditLogAccess: AccessLevel;
  allowAuditLogExport: boolean;
  enablePostingLog: boolean;
  viewPostingLogAccess: AccessLevel;
  allowPostingLogExport: boolean;
  allowPostingLogRetry: boolean;
  // Management
  viewEmployeeDirectory: boolean;
  viewEmployeeDirectoryAccess: AccessLevel;
  viewEmployeeDetails: boolean;
  // Data & Integration
  viewIntegrations: boolean;
  manageIntegrations: boolean;
  viewCompanies: boolean;
  manageCompanies: boolean;
  // Administration
  manageGeneralSettings: GeneralSettingsAccess;
  manageDeadlines: boolean;
  managePolicySettings: boolean;
  manageUtilisationSettings: boolean;
  manageTimeEntrySettings: boolean;
  manageWorkTypeSettings: boolean;
  manageApprovalsSettings: boolean;
  manageNotifications: boolean;
  manageRolesPermissions: boolean;
  manageRates: boolean;
  // manageScreenDataModes — hidden from UI pending Settings menu visibility decision
  manageScreenDataModes: boolean;
}

export type PermissionAction =
  | { type: 'SET_PERMISSION'; key: keyof PermissionState; value: PermissionState[keyof PermissionState] }
  | { type: 'SET_ALL'; permissions: Partial<PermissionState> }
  | { type: 'RESET'; defaults?: PermissionState };

export const createDefaultPermissionState = (): PermissionState => ({
  enabledPermissions: new Set<string>(),
  viewTimesheetsAccess: 'own',
  editTimesheetsAccess: 'own',
  allowDeleteTimesheets: false,
  allowDuplicateTimesheets: false,
  allowRecallTimesheet: false,
  enableCopyFromLastWeek: false,
  enableCopyDescription: false,
  enableCopyOnlyBillable: false,
  enableAutoSmartFill: false,
  enableAiInsights: false,
  viewApprovalDashboard: false,
  viewApprovals: false,
  editTimesheetsApproval: false,
  rejectTimesheets: false,
  compulsoryRejectionReason: false,
  bulkApprove: false,
  delegateApproval: false,
  viewPolicyViolations: false,
  viewDetectedIssues: false,
  enableTimesheetReport: false,
  viewTimesheetReportAccess: 'own',
  allowTimesheetReportExport: false,
  enableMissingTimesheetReport: false,
  viewMissingTimesheetReportAccess: 'own',
  allowMissingTimesheetReportExport: false,
  enableUtilisationReport: false,
  viewUtilisationReportAccess: 'own',
  allowUtilisationReportExport: false,
  enableAuditLog: false,
  viewAuditLogAccess: 'own',
  allowAuditLogExport: false,
  enablePostingLog: false,
  viewPostingLogAccess: 'own',
  allowPostingLogExport: false,
  allowPostingLogRetry: false,
  viewEmployeeDirectory: false,
  viewEmployeeDirectoryAccess: 'own',
  viewEmployeeDetails: false,
  viewIntegrations: false,
  manageIntegrations: false,
  viewCompanies: false,
  manageCompanies: false,
  manageGeneralSettings: 'view',
  manageDeadlines: false,
  managePolicySettings: false,
  manageUtilisationSettings: false,
  manageTimeEntrySettings: false,
  manageWorkTypeSettings: false,
  manageApprovalsSettings: false,
  manageNotifications: false,
  manageRolesPermissions: false,
  manageRates: false,
  manageScreenDataModes: false,
});

export const permissionReducer = (state: PermissionState, action: PermissionAction): PermissionState => {
  switch (action.type) {
    case 'SET_PERMISSION':
      return { ...state, [action.key]: action.value };
    case 'SET_ALL':
      return { ...state, ...action.permissions };
    case 'RESET':
      return action.defaults ?? createDefaultPermissionState();
    default:
      return state;
  }
};

/**
 * Maps API role permission data onto a partial PermissionState.
 * Sections absent from the payload are omitted so existing values are retained.
 */
export const buildPermissionStateFromData = (data: UserRolePermissions): Partial<PermissionState> => {
  const next: Partial<PermissionState> = {};

  if (data.permissions && Array.isArray(data.permissions)) {
    next.enabledPermissions = new Set(data.permissions);
  }
  if (data.timesheetAccess) {
    next.viewTimesheetsAccess = data.timesheetAccess.viewAccess || 'own';
    next.editTimesheetsAccess = data.timesheetAccess.editAccess || 'own';
    next.allowDeleteTimesheets = data.timesheetAccess.allowDelete || false;
    next.allowDuplicateTimesheets = data.timesheetAccess.allowDuplicate || false;
    next.allowRecallTimesheet = data.timesheetAccess.allowRecall || false;
  }
  if (data.smartFeaturesPermissions) {
    next.enableCopyFromLastWeek = data.smartFeaturesPermissions.enableCopyFromLastWeek || false;
    next.enableCopyDescription = data.smartFeaturesPermissions.enableCopyDescription || false;
    next.enableCopyOnlyBillable = data.smartFeaturesPermissions.enableCopyOnlyBillable || false;
    next.enableAutoSmartFill = data.smartFeaturesPermissions.enableAutoSmartFill || false;
    next.enableAiInsights = data.smartFeaturesPermissions.enableAiInsights || false;
  }
  next.viewApprovalDashboard = data.approvalPermissions?.viewDashboard || false;
  if (data.timesheetApprovalPermissions) {
    next.viewApprovals = data.timesheetApprovalPermissions.viewApprovals || false;
    next.editTimesheetsApproval = data.timesheetApprovalPermissions.editTimesheets || false;
    next.rejectTimesheets = data.timesheetApprovalPermissions.rejectTimesheets || false;
    next.compulsoryRejectionReason = data.timesheetApprovalPermissions.compulsoryRejectionReason || false;
    next.bulkApprove = data.timesheetApprovalPermissions.bulkApprove || false;
    next.delegateApproval = data.timesheetApprovalPermissions.delegateApproval || false;
    next.viewPolicyViolations = data.timesheetApprovalPermissions.viewPolicyViolations || false;
    next.viewDetectedIssues = data.timesheetApprovalPermissions.viewDetectedIssues || false;
  }
  if (data.reportAccessPermissions) {
    next.enableTimesheetReport = data.reportAccessPermissions.timesheetReport?.enabled || false;
    next.viewTimesheetReportAccess = data.reportAccessPermissions.timesheetReport?.viewAccess || 'own';
    next.allowTimesheetReportExport = data.reportAccessPermissions.timesheetReport?.allowExport || false;
    next.enableMissingTimesheetReport = data.reportAccessPermissions.missingTimesheetReport?.enabled || false;
    next.viewMissingTimesheetReportAccess = data.reportAccessPermissions.missingTimesheetReport?.viewAccess || 'own';
    next.allowMissingTimesheetReportExport = data.reportAccessPermissions.missingTimesheetReport?.allowExport || false;
    next.enableUtilisationReport = data.reportAccessPermissions.utilisationReport?.enabled || false;
    next.viewUtilisationReportAccess = data.reportAccessPermissions.utilisationReport?.viewAccess || 'own';
    next.allowUtilisationReportExport = data.reportAccessPermissions.utilisationReport?.allowExport || false;
    next.enableAuditLog = data.reportAccessPermissions.auditLog?.enabled || false;
    next.viewAuditLogAccess = data.reportAccessPermissions.auditLog?.viewAccess || 'own';
    next.allowAuditLogExport = data.reportAccessPermissions.auditLog?.allowExport || false;
    next.enablePostingLog = data.reportAccessPermissions.postingLog?.enabled || false;
    next.viewPostingLogAccess = data.reportAccessPermissions.postingLog?.viewAccess || 'own';
    next.allowPostingLogExport = data.reportAccessPermissions.postingLog?.allowExport || false;
    next.allowPostingLogRetry = data.reportAccessPermissions.postingLog?.allowRetry || false;
    // billableHoursReport removed — belongs in the Projects module of the PSA suite.
  }
  if (data.employeeDirectoryPermissions) {
    next.viewEmployeeDirectory = data.employeeDirectoryPermissions.viewDirectory || false;
    next.viewEmployeeDirectoryAccess = data.employeeDirectoryPermissions.viewAccess || 'own';
    next.viewEmployeeDetails = data.employeeDirectoryPermissions.viewDetails || false;
  }
  if (data.integrationsPermissions) {
    next.viewIntegrations = data.integrationsPermissions.viewIntegrations || false;
    next.manageIntegrations = data.integrationsPermissions.manageIntegrations || false;
  }
  if (data.companyManagementPermissions) {
    next.viewCompanies = data.companyManagementPermissions.viewCompanies || false;
    next.manageCompanies = data.companyManagementPermissions.manageCompanies || false;
  }
  if (data.systemSettingsPermissions) {
    next.manageGeneralSettings = data.systemSettingsPermissions.manageGeneralSettings || 'view';
    next.manageDeadlines = data.systemSettingsPermissions.manageDeadlines || false;
    next.managePolicySettings = data.systemSettingsPermissions.managePolicySettings || false;
    next.manageUtilisationSettings = data.systemSettingsPermissions.manageUtilisationSettings || false;
    next.manageTimeEntrySettings = data.systemSettingsPermissions.manageTimeEntrySettings || false;
    next.manageWorkTypeSettings = data.systemSettingsPermissions.manageWorkTypeSettings || false;
    next.manageApprovalsSettings = data.systemSettingsPermissions.manageApprovalsSettings || false;
    next.manageNotifications = data.systemSettingsPermissions.manageNotifications || false;
    next.manageRolesPermissions = data.systemSettingsPermissions.manageRolesPermissions || false;
    next.manageRates = data.systemSettingsPermissions.manageRates || false;
    next.manageScreenDataModes = data.systemSettingsPermissions.manageScreenDataModes || false;
  }

  return next;
};

/**
 * Maps a default role template onto a partial PermissionState.
 * Mirrors the previous inline "Apply Default Template" behaviour.
 */
export const buildPermissionStateFromDefaults = (defaults: UserRolePermissions): Partial<PermissionState> => {
  const next: Partial<PermissionState> = {
    enabledPermissions: new Set(defaults.permissions ?? []),
    viewApprovalDashboard: defaults.approvalPermissions?.viewDashboard ?? false,
  };

  if (defaults.timesheetAccess) {
    next.viewTimesheetsAccess = defaults.timesheetAccess.viewAccess;
    next.editTimesheetsAccess = defaults.timesheetAccess.editAccess;
    next.allowDeleteTimesheets = defaults.timesheetAccess.allowDelete;
    next.allowDuplicateTimesheets = defaults.timesheetAccess.allowDuplicate;
    next.allowRecallTimesheet = defaults.timesheetAccess.allowRecall;
  }
  if (defaults.smartFeaturesPermissions) {
    next.enableCopyFromLastWeek = defaults.smartFeaturesPermissions.enableCopyFromLastWeek;
    next.enableCopyDescription = defaults.smartFeaturesPermissions.enableCopyDescription;
    next.enableCopyOnlyBillable = defaults.smartFeaturesPermissions.enableCopyOnlyBillable;
    next.enableAutoSmartFill = defaults.smartFeaturesPermissions.enableAutoSmartFill;
    next.enableAiInsights = defaults.smartFeaturesPermissions.enableAiInsights;
  }
  if (defaults.timesheetApprovalPermissions) {
    next.viewApprovals = defaults.timesheetApprovalPermissions.viewApprovals;
    next.editTimesheetsApproval = defaults.timesheetApprovalPermissions.editTimesheets;
    next.rejectTimesheets = defaults.timesheetApprovalPermissions.rejectTimesheets;
    next.compulsoryRejectionReason = defaults.timesheetApprovalPermissions.compulsoryRejectionReason;
    next.bulkApprove = defaults.timesheetApprovalPermissions.bulkApprove;
    next.delegateApproval = defaults.timesheetApprovalPermissions.delegateApproval;
    next.viewPolicyViolations = defaults.timesheetApprovalPermissions.viewPolicyViolations;
    next.viewDetectedIssues = defaults.timesheetApprovalPermissions.viewDetectedIssues;
  }
  if (defaults.reportAccessPermissions) {
    next.enableTimesheetReport = defaults.reportAccessPermissions.timesheetReport?.enabled ?? false;
    next.viewTimesheetReportAccess = defaults.reportAccessPermissions.timesheetReport?.viewAccess ?? 'own';
    next.allowTimesheetReportExport = defaults.reportAccessPermissions.timesheetReport?.allowExport ?? false;
    next.enableMissingTimesheetReport = defaults.reportAccessPermissions.missingTimesheetReport?.enabled ?? false;
    next.viewMissingTimesheetReportAccess = defaults.reportAccessPermissions.missingTimesheetReport?.viewAccess ?? 'own';
    next.allowMissingTimesheetReportExport = defaults.reportAccessPermissions.missingTimesheetReport?.allowExport ?? false;
    next.enableUtilisationReport = defaults.reportAccessPermissions.utilisationReport?.enabled ?? false;
    next.viewUtilisationReportAccess = defaults.reportAccessPermissions.utilisationReport?.viewAccess ?? 'own';
    next.allowUtilisationReportExport = defaults.reportAccessPermissions.utilisationReport?.allowExport ?? false;
    next.enableAuditLog = defaults.reportAccessPermissions.auditLog?.enabled ?? false;
    next.viewAuditLogAccess = defaults.reportAccessPermissions.auditLog?.viewAccess ?? 'own';
    next.allowAuditLogExport = defaults.reportAccessPermissions.auditLog?.allowExport ?? false;
    next.enablePostingLog = defaults.reportAccessPermissions.postingLog?.enabled ?? false;
    next.viewPostingLogAccess = defaults.reportAccessPermissions.postingLog?.viewAccess ?? 'own';
    next.allowPostingLogExport = defaults.reportAccessPermissions.postingLog?.allowExport ?? false;
    next.allowPostingLogRetry = defaults.reportAccessPermissions.postingLog?.allowRetry ?? false;
    // billableHoursReport removed — belongs in the Projects module of the PSA suite.
  }
  if (defaults.employeeDirectoryPermissions) {
    next.viewEmployeeDirectory = defaults.employeeDirectoryPermissions.viewDirectory;
    next.viewEmployeeDirectoryAccess = defaults.employeeDirectoryPermissions.viewAccess || 'own';
    next.viewEmployeeDetails = defaults.employeeDirectoryPermissions.viewDetails;
  }
  if (defaults.integrationsPermissions) {
    next.viewIntegrations = defaults.integrationsPermissions.viewIntegrations;
    next.manageIntegrations = defaults.integrationsPermissions.manageIntegrations;
  }
  if (defaults.companyManagementPermissions) {
    next.viewCompanies = defaults.companyManagementPermissions.viewCompanies;
    next.manageCompanies = defaults.companyManagementPermissions.manageCompanies;
  }
  if (defaults.systemSettingsPermissions) {
    next.manageGeneralSettings = defaults.systemSettingsPermissions.manageGeneralSettings;
    next.manageDeadlines = defaults.systemSettingsPermissions.manageDeadlines;
    next.managePolicySettings = defaults.systemSettingsPermissions.managePolicySettings;
    next.manageUtilisationSettings = defaults.systemSettingsPermissions.manageUtilisationSettings;
    next.manageTimeEntrySettings = defaults.systemSettingsPermissions.manageTimeEntrySettings ?? false;
    next.manageWorkTypeSettings = defaults.systemSettingsPermissions.manageWorkTypeSettings ?? false;
    next.manageApprovalsSettings = defaults.systemSettingsPermissions.manageApprovalsSettings;
    next.manageNotifications = defaults.systemSettingsPermissions.manageNotifications;
    next.manageRolesPermissions = defaults.systemSettingsPermissions.manageRolesPermissions;
    next.manageRates = defaults.systemSettingsPermissions.manageRates ?? false;
    next.manageScreenDataModes = defaults.systemSettingsPermissions.manageScreenDataModes;
  }

  return next;
};
